import { api } from '@/config/api';

export type CustomFieldType =
  | 'text' | 'textarea' | 'number' | 'money' | 'date' | 'datetime'
  | 'boolean' | 'select' | 'multiselect' | 'email' | 'phone' | 'url'
  | 'media';

/** Per-media-field cap (mirrors backend MAX_MEDIA_ITEMS_PER_FIELD). */
export const MAX_MEDIA_ITEMS_PER_FIELD = 10;

export interface MediaItem {
  kind: 'image' | 'video';
  url: string;
  thumbnailUrl?: string;
  fileName?: string;
}

export interface CustomFieldDef {
  key: string;
  label: string;
  type: CustomFieldType;
  placeholder?: string;
  required?: boolean;
  description?: string;
  options?: string[];
  includeInAiContext?: boolean;
  order?: number;
}

export interface CustomObjectType {
  _id: string;
  companyUid: string;
  key: string;
  label: string;
  pluralLabel?: string;
  icon?: string;
  description?: string;
  linkToContact: boolean;
  linkToLead: boolean;
  fields: CustomFieldDef[];
  installedFromTemplate?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomObjectRecord {
  _id: string;
  recordUid: string;
  companyUid: string;
  typeKey: string;
  contactUid?: string;
  leadUid?: string;
  title?: string;
  values: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface VerticalTemplateSummary {
  id: string;
  industries: string[];
  label: string;
  description: string;
  types: Array<{
    key: string;
    label: string;
    pluralLabel: string;
    icon: string;
    description: string;
    fieldCount: number;
  }>;
}

export interface TemplateListResponse {
  /** The industry the recommendations were filtered by (null when unset). */
  industry: string | null;
  /** Templates that match the company's industry. */
  recommended: VerticalTemplateSummary[];
  /** Everything else. */
  other: VerticalTemplateSummary[];
}

export const customObjectsService = {
  // Templates
  listTemplates: async (industryOverride?: string): Promise<TemplateListResponse> =>
    (await api.get('/slighter_crm/custom-objects/templates', {
      params: industryOverride ? { industry: industryOverride } : {},
    })).data,
  installTemplate: async (templateId: string): Promise<{ installed: string[]; skipped: string[] }> =>
    (await api.post('/slighter_crm/custom-objects/templates/install', { templateId })).data,

  // Types
  listTypes: async (includeInactive = false): Promise<CustomObjectType[]> =>
    (await api.get('/slighter_crm/custom-objects/types', { params: includeInactive ? { includeInactive: '1' } : {} })).data,
  getType: async (key: string): Promise<CustomObjectType> =>
    (await api.get(`/slighter_crm/custom-objects/types/${key}`)).data,
  createType: async (payload: Partial<CustomObjectType>): Promise<CustomObjectType> =>
    (await api.post('/slighter_crm/custom-objects/types', payload)).data,
  updateType: async (key: string, payload: Partial<CustomObjectType>): Promise<CustomObjectType> =>
    (await api.patch(`/slighter_crm/custom-objects/types/${key}`, payload)).data,
  deleteType: async (key: string): Promise<{ ok: boolean }> =>
    (await api.delete(`/slighter_crm/custom-objects/types/${key}`)).data,

  /**
   * Generate a draft custom-object type via AI from a free-text description.
   * Returns a proposal — nothing is saved until the operator clicks Save in
   * the form. Backend sanitises types/keys/option-lists before returning.
   */
  generateTypeViaAi: async (payload: { description: string; industry?: string }): Promise<{
    label: string;
    pluralLabel?: string;
    key: string;
    icon: string;
    description?: string;
    linkToContact: boolean;
    linkToLead: boolean;
    fields: CustomFieldDef[];
  }> =>
    (await api.post('/slighter_crm/custom-objects/types/generate-via-ai', payload)).data,

  // Records
  listRecords: async (params: {
    typeKey?: string; contactUid?: string; leadUid?: string; q?: string; page?: number; limit?: number;
  } = {}): Promise<{ items: CustomObjectRecord[]; total: number; page: number; limit: number }> =>
    (await api.get('/slighter_crm/custom-objects/records', { params })).data,
  getRecord: async (recordUid: string): Promise<CustomObjectRecord> =>
    (await api.get(`/slighter_crm/custom-objects/records/${recordUid}`)).data,
  createRecord: async (payload: {
    typeKey: string; title?: string; contactUid?: string; leadUid?: string; values: Record<string, unknown>;
  }): Promise<CustomObjectRecord> =>
    (await api.post('/slighter_crm/custom-objects/records', payload)).data,
  updateRecord: async (recordUid: string, payload: {
    title?: string; contactUid?: string; leadUid?: string; values?: Record<string, unknown>;
  }): Promise<CustomObjectRecord> =>
    (await api.patch(`/slighter_crm/custom-objects/records/${recordUid}`, payload)).data,
  deleteRecord: async (recordUid: string): Promise<{ ok: boolean }> =>
    (await api.delete(`/slighter_crm/custom-objects/records/${recordUid}`)).data,

  importRecords: async (
    typeKey: string,
    rows: Array<{
      title?: string;
      contactUid?: string;
      leadUid?: string;
      values: Record<string, unknown>;
    }>,
  ): Promise<{ inserted: number; skipped: number; errors: Array<{ row: number; error: string }> }> =>
    (await api.post('/slighter_crm/custom-objects/records/import', { typeKey, rows })).data,
};
